import React, { useState } from "react";
import { MoreVertical, Flag, Ban, Trash2, ShieldAlert } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const REASONS = [
  { value: "contenu_inapproprie", label: "Contenu inapproprié" },
  { value: "harcelement", label: "Harcèlement" },
  { value: "arnaque", label: "Arnaque / fraude" },
  { value: "mineur", label: "Contenu impliquant un mineur" },
  { value: "autre", label: "Autre" },
];

const SafetyActions = ({ targetUser, photo, canDelete, onDeleted, onBlocked, align = "end" }) => {
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reason, setReason] = useState(REASONS[0].value);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submitReport = async () => {
    setBusy(true);
    try {
      const target_type = photo ? "photo" : "user";
      const target_id = photo ? photo.id : targetUser?.id;
      const { data } = await api.post("/reports", { target_type, target_id, reason, details });
      toast.success(data.message);
      setReportOpen(false);
      setDetails("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitBlock = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${encodeURIComponent(targetUser.username)}/block`);
      toast.success("Utilisateur bloqué");
      setBlockOpen(false);
      onBlocked && onBlocked();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/photos/${photo.id}`);
      toast.success("Photo supprimée");
      setDeleteOpen(false);
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="h-8 w-8 grid place-items-center rounded-lg hover:bg-secondary transition-colors"
            data-testid="safety-menu-trigger"
            aria-label="Options"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-48">
          <DropdownMenuItem data-testid="safety-report-button" onClick={() => setReportOpen(true)}>
            <Flag className="h-4 w-4 mr-2" /> Signaler
          </DropdownMenuItem>
          {canDelete && photo && (
            <DropdownMenuItem
              data-testid="safety-delete-photo-button"
              className="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Supprimer la photo
            </DropdownMenuItem>
          )}
          {targetUser && (
            <DropdownMenuItem
              data-testid="safety-block-user-button"
              className="text-destructive focus:text-destructive"
              onClick={() => setBlockOpen(true)}
            >
              <Ban className="h-4 w-4 mr-2" /> Bloquer l'utilisateur
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <ShieldAlert className="h-5 w-5" /> Signaler {photo ? "cette photo" : "cet utilisateur"}
            </DialogTitle>
            <DialogDescription>Aidez-nous à garder la communauté sûre.</DialogDescription>
          </DialogHeader>
          <RadioGroup value={reason} onValueChange={setReason} className="space-y-1">
            {REASONS.map((r) => (
              <div key={r.value} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-secondary">
                <RadioGroupItem value={r.value} id={r.value} data-testid={`report-reason-${r.value}`} />
                <Label htmlFor={r.value} className="font-normal cursor-pointer flex-1">{r.label}</Label>
              </div>
            ))}
          </RadioGroup>
          <Textarea
            placeholder="Détails (facultatif)"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            data-testid="report-details"
            className="rounded-xl"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportOpen(false)}>Annuler</Button>
            <Button onClick={submitReport} disabled={busy} data-testid="report-submit">Envoyer le signalement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block confirm */}
      <AlertDialog open={blockOpen} onOpenChange={setBlockOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Bloquer @{targetUser?.username} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous ne recevrez plus ses photos ni ses messages, et il ne pourra plus vous contacter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={submitBlock} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-block">
              Bloquer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette photo ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={submitDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SafetyActions;
